import React, {
  Fragment,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState
} from 'react';
import { FormContext, useForm } from 'react-hook-form';
import { Prompt } from 'react-router';
import Box from '@material-ui/core/Box';
import Button from '@material-ui/core/Button';
import CircularProgress from '@material-ui/core/CircularProgress';
import Drawer from '@material-ui/core/Drawer';
import FormControl from '@material-ui/core/FormControl';
import FormHelperText from '@material-ui/core/FormHelperText';
import Tooltip from '@material-ui/core/Tooltip';
import Typography from '@material-ui/core/Typography';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import {
  normalizeDraftSafeAttachments,
  removeLegacyDraftById,
  useAutosaveDraft,
  useDrafts
} from 'features/drafts';
import { NEW_JOB_ORDER_ENDPOINTS } from 'features/new-job-order/constants';
import { useGlobalStyles } from 'GlobalStyles';
import { useShowAlert } from 'hooks';
import useUnload from 'hooks/unload';
import flatMap from 'lodash/flatMap';
import map from 'lodash/map';
import { DraftsProviderContext, milisecondsToSave } from 'providers/DraftsProvider';
import queryString from 'query-string';
import { EntityRoutes } from 'routes/constants';
import API from 'services/API';
import { userHasRole } from 'services/Authorization';
import strings from 'strings';
import FPActionButton from 'UI/components/atoms/FPActionButton';
import { FPCard, FPCardContent, FPCardHeader } from 'UI/components/atoms/FPCard';
import { When } from 'UI/components/atoms/When';
import AutocompleteSelect from 'UI/components/molecules/AutocompleteSelect';
import FPButtonBuilder from 'UI/components/molecules/FPButtonBuilder';
import FileManager from 'UI/components/organisms/FileManager';
import HiringAuthorityDrawer from 'UI/components/organisms/HiringAuthorityDrawer';
import JobOrderForm from 'UI/components/organisms/JobOrderForm';
import { FormFieldsMap } from 'UI/components/organisms/JobOrderForm/fields';
import {
  DefaultNewJobOrderFileCategories,
  drawerAnchor,
  waitingTimeBeforeRedirect
} from 'UI/constants/defaults';
import { Endpoints } from 'UI/constants/endpoints';
import { EntityType, JobOrderEntity, TabKeys } from 'UI/constants/entityTypes';
import { Roles } from 'UI/constants/roles';
import { AssignmentSheetDrawer } from 'UI/pages/NewJobOrder/components/AssigmentSheetDrawer';
import { decryptId } from 'UI/utils/encrypt';
import { prepareFormToSubmit } from 'UI/utils/forms';
import { Selectors } from 'UI/utils/renderers';

import { useStyles } from './NewJobOrderForm.styled';

const chainedSelects = {
  company_id: [FormFieldsMap.HiringAuthority.key]
};

const DELETE_API_VERSION = 1;
const assignmentSheetVersionsStrings = strings.inventory.jobOrders.create.assignmentSheet;

/**
 * @param {Object} props
 * @param {*} props.history
 * @param {*} props.location
 */
const buildJobOrderDraftBody = (draftData, entity) => ({
  entity,
  ...draftData,
  title: draftData.formData?.title,
  companyName: draftData.formData?.company?.name,
  companyId: draftData.formData?.company?.id
});

const getInitialFiles = ({ attachments, draft, isDatabaseDrafts }) => {
  if (isDatabaseDrafts) return draft?.attachments ?? [];
  if (draft?.id) return draft.attachments;
  return attachments;
};

const getInitialFormValues = ({ draft, isDatabaseDrafts }) => {
  if (isDatabaseDrafts) return draft?.formData ?? {};
  return draft?.id ? draft.formData : {};
};

export const NewJobOrder = ({
  databaseDraft,
  deletedDraftId,
  location,
  history,
  onDraftSaved,
  onResetForm
}) => {
  const classes = useStyles();
  const { showAlert } = useShowAlert();
  const [attachments, setAttachments] = useState(databaseDraft?.attachments ?? []);

  const assignmentSheetStrings = assignmentSheetVersionsStrings.new;

  const { companyId } = useMemo(() => {
    try {
      const queryParams = queryString.parse(location.search);
      const { company } = queryParams;
      return {
        companyId: company ? decryptId(company, true)[0] : queryParams?.companyId
      };
    } catch (error) {
      showAlert({
        title: 'Invalid Company Reference.',
        error
      });
      return {};
    }
  }, [location.search, showAlert]);

  const legacyDraftController = useContext(DraftsProviderContext);
  const isDatabaseDrafts = databaseDraft !== undefined;
  const {
    hasRequiredFieldsForDraft,
    draft: legacyDraft,
    drafts,
    saveDraft: saveLegacyDraft,
    updateDraft: updateLegacyDraft,
    deleteDraftById: deleteLegacyDraftById,
    setDraft,
    saveOnCancel,
    updateLocalStorageDrafts
  } = legacyDraftController || {};

  const initialDatabaseDraft = databaseDraft || {
    formData: {},
    attachments: [],
    sheetData: null,
    assignmentSheet: null
  };
  const draft = isDatabaseDrafts ? initialDatabaseDraft : legacyDraft;

  const initialFiles = getInitialFiles({ attachments, draft, isDatabaseDrafts });

  const [whitesheet] = useState(draft?.sheetData ?? null);
  const assignmentSheet = useRef(draft?.assignmentSheet ?? null);
  const [hasHAOptions, setHasHAOptions] = useState(null);
  const isUserCoach = userHasRole(Roles.Coach);

  const form = useForm({
    defaultValues: getInitialFormValues({ draft, isDatabaseDrafts })
  });

  const { register, unregister, setValue, handleSubmit, getValues, watch } = form;
  const formValues = watch();

  const databaseDraftController = useDrafts({
    buildBody: buildJobOrderDraftBody,
    initialDraft: initialDatabaseDraft,
    entity: isDatabaseDrafts ? EntityType.Joborder : null,
    endpoint: isDatabaseDrafts ? NEW_JOB_ORDER_ENDPOINTS.drafts : null,
    onSaved: onDraftSaved
  });

  const getDatabaseDraftValues = useCallback(
    () => ({
      formData: getValues(),
      assignmentSheet: assignmentSheet.current,
      sheetData: whitesheet
    }),
    [getValues, whitesheet]
  );
  const canSaveDatabaseDraft = Boolean(formValues[FormFieldsMap.Position.key]);
  const draftSafeAttachments = useMemo(
    () => normalizeDraftSafeAttachments(attachments),
    [attachments]
  );
  const autosaveValues = getDatabaseDraftValues();
  const databaseAutosave = useAutosaveDraft({
    attachments: draftSafeAttachments,
    autosaveValues: isDatabaseDrafts ? autosaveValues : null,
    canSaveDraft: isDatabaseDrafts && canSaveDatabaseDraft,
    getFormValues: getDatabaseDraftValues,
    getPendingSavePromise: databaseDraftController.getPendingSavePromise,
    lastSavedSnapshotRef: databaseDraftController.lastSavedSnapshotRef,
    saveDraft: databaseDraftController.saveDraft
  });
  const {
    deleteDraftById: deleteDatabaseDraftById,
    detachDraft: detachDatabaseDraft,
    draftId: databaseDraftId,
    draftUpdatedAt: databaseDraftUpdatedAt,
    isSavingDraft: isSavingDatabaseDraft,
    saveDraft: saveDatabaseDraft,
    updateDraft: updateDatabaseDraft
  } = databaseDraftController;
  const { cancelAutosave, flushAutosave, hasUnsavedChanges } = databaseAutosave;

  useEffect(() => {
    if (!deletedDraftId || databaseDraftId !== deletedDraftId) return;

    cancelAutosave();
    detachDatabaseDraft(deletedDraftId);
  }, [cancelAutosave, databaseDraftId, deletedDraftId, detachDatabaseDraft]);

  const globalClasses = useGlobalStyles();

  const currentCompany = formValues[FormFieldsMap.Company.key];
  const currentCompanyId = currentCompany?.id;
  const [uiState, setUiState] = useState({
    isSaving: false,
    isSuccess: false,
    isWhitesheetOpen: false,
    isHAOpen: false
  });
  const isHiringAuthorityEnabled = !!currentCompany;
  const selectedRecruiter = watch(FormFieldsMap.Recruiter.key);

  useEffect(() => {
    const validation = isHiringAuthorityEnabled
      ? { required: 'Hiring Authority is required' }
      : undefined;
    register({ name: FormFieldsMap.HiringAuthority.key }, validation);
  }, [isHiringAuthorityEnabled, register]);

  useEffect(() => {
    if (isUserCoach) register({ name: FormFieldsMap.Recruiter.key });
    return () => unregister(FormFieldsMap.Recruiter.key);
  }, [register, unregister, isUserCoach]);

  const toggleDrawer = (drawer, open) => event => {
    if (event && event.type === 'keydown' && (event.key === 'Tab' || event.key === 'Shift')) {
      return;
    }
    setUiState(prevState => ({ ...prevState, [drawer]: open }));

    if (!isDatabaseDrafts && assignmentSheet.current) {
      setDraft(prev => ({ ...prev, assignmentSheet: assignmentSheet.current }));
    }
  };

  const handleValuesChanged = useCallback(
    values => {
      if (isDatabaseDrafts) {
        updateDatabaseDraft({ formData: values });
        return;
      }
      setDraft(prev => ({ ...prev, formData: values }));
    },
    [isDatabaseDrafts, setDraft, updateDatabaseDraft]
  );

  useEffect(() => {
    if (draft?.assignmentSheet?.id) {
      if (!draft?.assignmentSheet?.progress) {
        setValue('whitesheet_completed', undefined, true);
        return;
      }

      if (draft?.assignmentSheet?.progress.answered !== draft?.assignmentSheet?.progress.total) {
        setValue('whitesheet_completed', undefined, true);
        return;
      }

      setValue('whitesheet_completed', true, true);
    }
  }, [draft, setValue]);

  const handleUpdateDraft = useCallback(() => {
    if (isDatabaseDrafts) {
      cancelAutosave();
      return saveDatabaseDraft({
        ...getDatabaseDraftValues(),
        formData: getValues(),
        attachments: draftSafeAttachments
      });
    }
    return updateLegacyDraft({ ...draft, formData: getValues() });
  }, [
    cancelAutosave,
    draftSafeAttachments,
    draft,
    getDatabaseDraftValues,
    getValues,
    isDatabaseDrafts,
    saveDatabaseDraft,
    updateLegacyDraft
  ]);

  const saveOrUpdate = useCallback(() => {
    if (!isDatabaseDrafts && hasRequiredFieldsForDraft && !uiState.isSuccess) {
      draft.id ? handleUpdateDraft(draft) : saveLegacyDraft({ shouldConfirm: false });
    }
  }, [
    draft,
    handleUpdateDraft,
    hasRequiredFieldsForDraft,
    isDatabaseDrafts,
    saveLegacyDraft,
    uiState.isSuccess
  ]);

  useUnload(e => {
    e.preventDefault();
    if (isDatabaseDrafts) {
      flushAutosave();
    } else {
      saveOrUpdate();
    }
  });

  useEffect(() => {
    if (isDatabaseDrafts) return undefined;
    const interval = setInterval(() => {
      saveOrUpdate();
    }, milisecondsToSave);
    return () => {
      clearInterval(interval);
    };
  }, [isDatabaseDrafts, saveOrUpdate]);

  const refreshHiringAuthorities = async () => {
    await API.get(`${Endpoints.Companies}/${currentCompanyId}/${Endpoints.HiringAuthorities}`)
      .then(response => {
        if (response.status === HTTPStatusCodes.Ok) setHasHAOptions(response.data);
      })
      .catch(error => {
        showAlert({
          title: 'Hiring Authority',
          error,
          autoHideDuration: 5000
        });
      });
  };

  const handleHiringAuthorityCompleted = newHiringAuthority => {
    setUiState(prevState => ({
      ...prevState,
      isHAOpen: false
    }));
    refreshHiringAuthorities();
    handleComboChange(FormFieldsMap.HiringAuthority.key, newHiringAuthority);
  };

  const handleFilesChanged = useCallback(
    data => {
      const attachmentsChanged = flatMap(map(data, 'files'));
      const safeAttachments = normalizeDraftSafeAttachments(attachmentsChanged);
      setAttachments(attachmentsChanged);
      if (isDatabaseDrafts) {
        updateDatabaseDraft({ attachments: safeAttachments });
      } else {
        setDraft(prev => ({ ...prev, attachments: attachmentsChanged }));
      }
    },
    [isDatabaseDrafts, setDraft, updateDatabaseDraft]
  );

  const handleCancelClick = () => {
    if (isDatabaseDrafts) {
      flushAutosave();
      history.goBack();
      return;
    }
    hasRequiredFieldsForDraft ? saveOnCancel() : history.goBack();
  };

  const handleComboChange = (name, value) => {
    setValue(name, value, true);

    if (name && chainedSelects[name]) {
      chainedSelects[name].forEach(chainedSelect => {
        setValue(chainedSelect, null);
      });
    }
  };

  const handleSaveAssignmentSheet = (assignmentSheetID, progress) => {
    assignmentSheet.current = { id: assignmentSheetID, progress };
    const isComplete = progress && progress.answered === progress.total;
    setValue('whitesheet_completed', isComplete ? true : undefined, true);

    if (isDatabaseDrafts) {
      const updates = {
        ...getDatabaseDraftValues(),
        assignmentSheet: assignmentSheet.current
      };
      updateDatabaseDraft(updates);
      return;
    }

    const draftToUpdate = { ...draft, assignmentSheet: { id: assignmentSheetID, progress } };

    const newDrafts = drafts.map(draftItem =>
      draftItem.id === draftToUpdate.id ? draftToUpdate : draftItem
    );
    updateLocalStorageDrafts(newDrafts);
  };

  const onSubmit = async formData => {
    setUiState(prevState => ({ ...prevState, isSaving: true }));
    try {
      const persistedDraftId = isDatabaseDrafts ? await flushAutosave() : null;
      const preparedJobOrder = prepareFormToSubmit(formData, FormFieldsMap);
      const preparedSheet = {
        dataSheetId: (assignmentSheet.current ?? draft?.assignmentSheet)?.id
      };
      const candidateData = {
        ...preparedJobOrder,
        ...preparedSheet,
        files: draftSafeAttachments.map(att => att.id)
      };
      const response = await API.post(Endpoints.JobOrders, candidateData);
      if (response.data && response.status === 201) {
        setUiState(prevState => ({
          ...prevState,
          isSuccess: true
        }));
        showAlert({
          title: 'Job Orders',
          body: 'Awesome! The job order was created successfully'
        });

        const draftIdToDelete = persistedDraftId || databaseDraftId;
        if (isDatabaseDrafts && draftIdToDelete) {
          await deleteDatabaseDraftById(draftIdToDelete);
        } else if (draft?.id) {
          deleteLegacyDraftById(draft.id);
        }
        if (isDatabaseDrafts && draft?.legacyDraftId) {
          removeLegacyDraftById({
            draftId: draft.legacyDraftId,
            entity: EntityType.Joborder
          });
        }

        setTimeout(() => {
          companyId
            ? history.push(
                `${EntityRoutes.CompanyProfile.replace(':id', companyId)}?tab=${TabKeys.JobOrders}`
              )
            : history.push(EntityRoutes.JobOrderProfile.replace(':id', response.data.data.id));
        }, waitingTimeBeforeRedirect);
      }
    } catch (error) {
      setUiState(prevState => ({
        ...prevState,
        isSuccess: false
      }));
      showAlert({
        title: 'Job Order',
        autoHideDuration: 5000,
        error
      });
    } finally {
      setUiState(prevState => ({
        ...prevState,
        isSaving: false
      }));
    }
  };

  const handleRecruiterSelect = (name, value) => {
    setValue(name, value, true);
  };

  const handleResetClick = () => {
    if (isSavingDatabaseDraft || uiState.isSaving) return;

    // eslint-disable-next-line no-alert
    if (hasUnsavedChanges() && !window.confirm(strings.drafts.leaveForm)) return;

    cancelAutosave();
    onResetForm();
  };

  const canSaveCurrentDraft = isDatabaseDrafts ? canSaveDatabaseDraft : hasRequiredFieldsForDraft;
  const currentDraftId = isDatabaseDrafts ? databaseDraftId : draft?.id;
  const draftButtonText = currentDraftId
    ? strings.drafts.button.update
    : strings.drafts.button.save;
  const isSavingCurrentDraft = isDatabaseDrafts && isSavingDatabaseDraft;
  let saveDraftClickHandler = handleUpdateDraft;
  if (!isDatabaseDrafts && !draft?.id) {
    saveDraftClickHandler = () => saveLegacyDraft({ shouldConfirm: true });
  }

  const buttonDefinitions = [
    {
      props: {
        onClick: handleCancelClick,
        size: 'small',
        text: strings.shared.ui.cancel,
        variant: 'outlined',
        disabled: uiState.isSaving
      }
    },
    {
      props: {
        className: classes.saveDraftBtn,
        disabled: !canSaveCurrentDraft || uiState.isSaving || isSavingCurrentDraft,
        onClick: saveDraftClickHandler,
        text: isSavingCurrentDraft ? `${strings.shared.ui.saving} draft...` : draftButtonText,
        startIcon: isSavingCurrentDraft && <CircularProgress size={20} />,
        variant: 'outlined'
      }
    },
    {
      props: {
        disabled: uiState.isSaving,
        initialText: strings.shared.ui.save,
        isSaving: uiState.isSaving,
        isSuccess: uiState.isSuccess,
        onProgressText: strings.shared.ui.saving,
        type: 'submit',
        variant: 'contained'
      },
      mode: 'save'
    }
  ];

  return (
    <Fragment key={drawerAnchor}>
      <Prompt
        when={isDatabaseDrafts ? hasUnsavedChanges() : hasRequiredFieldsForDraft}
        message={isDatabaseDrafts ? strings.drafts.leaveForm : saveOrUpdate}
      />

      <When condition={!!onResetForm}>
        <Box display="flex" justifyContent="flex-end" marginBottom={1}>
          <Button
            color="primary"
            disabled={isSavingDatabaseDraft || uiState.isSaving}
            onClick={handleResetClick}
            variant="text"
          >
            {strings.formatString(strings.shared.ui.reset, { item: strings.shared.ui.form })}
          </Button>
        </Box>
      </When>

      <FormContext {...form}>
        <form onSubmit={handleSubmit(onSubmit)}>
          <div className={globalClasses.newItemsSection}>
            <FPCard>
              <FPCardHeader
                title="Job Order Data"
                subheader="Fields marked with * are required to add this Job Order"
                variant="section"
              />

              <FPCardContent variant="relaxed">
                <JobOrderForm companyId={companyId} onValuesChanged={handleValuesChanged} />
              </FPCardContent>
            </FPCard>
          </div>
          <div className={globalClasses.newItemsSection}>
            <FPCard>
              <FPCardHeader
                title="Hiring Authorities"
                subheader="A hiring authority related to this job order is required. Choose one"
                variant="section"
              />
              <FPCardContent variant="relaxed">
                <Tooltip
                  placement="top"
                  title={isHiringAuthorityEnabled ? '' : 'Please select a Company first'}
                >
                  <div className="inputContainer">
                    <AutocompleteSelect
                      createButton={{
                        text: 'Add Hiring Authority',
                        concatKeyword: false,
                        showAlways: true,
                        internalOptionsFiltering: true
                      }}
                      disabled={!isHiringAuthorityEnabled}
                      defaultOptions={hasHAOptions}
                      name={FormFieldsMap.HiringAuthority.key}
                      displayKey="full_name"
                      selectedValue={formValues[FormFieldsMap.HiringAuthority.key]}
                      placeholder="Hiring Authority *"
                      error={!!form.errors.hiring_authority_id}
                      errorText={
                        form.errors.hiring_authority_id && form.errors.hiring_authority_id.message
                      }
                      onSelect={handleComboChange}
                      getOptionLabel={option => option.full_name}
                      getOptionSelected={Selectors.byId}
                      onCreateButton={toggleDrawer('isHAOpen', true)}
                      url={
                        currentCompanyId &&
                        `${Endpoints.Companies}/${currentCompanyId}/${Endpoints.HiringAuthorities}`
                      }
                    />
                  </div>
                </Tooltip>
              </FPCardContent>
            </FPCard>
          </div>

          <div className={globalClasses.newItemsSection}>
            <FPCard>
              <FPCardHeader
                title={assignmentSheetStrings.title}
                subheader={assignmentSheetStrings.subtitle}
                variant="section"
              />

              <FPCardContent variant="relaxed">
                <FPActionButton
                  onClick={toggleDrawer('isWhitesheetOpen', true)}
                  text={whitesheet ? strings.shared.ui.edit : assignmentSheetStrings.actions.fillIn}
                  disabled={!currentCompanyId || !formValues?.title}
                />
                <input
                  ref={form.register({
                    required: assignmentSheetStrings.warningFormFill
                  })}
                  type="hidden"
                  name="whitesheet_completed"
                />
                {!!form.errors.whitesheet_completed && (
                  <div>
                    <FormControl component="fieldset" error={!!form.errors.whitesheet_completed}>
                      <FormHelperText>
                        {form.errors.whitesheet_completed &&
                          form.errors.whitesheet_completed.message}
                      </FormHelperText>
                    </FormControl>
                  </div>
                )}
              </FPCardContent>
            </FPCard>
          </div>

          <div className={globalClasses.newItemsSection}>
            <FPCard>
              <FPCardHeader
                title={strings.fileManager.newProfile.sectionTitle}
                subheader={strings.formatString(strings.fileManager.newProfile.sectionSubtitle, {
                  categorySuggested: 'job description'
                })}
                variant="section"
              />
              <FPCardContent variant="relaxed">
                <FileManager
                  apiVersionForDeleting={DELETE_API_VERSION}
                  defaultCategories={DefaultNewJobOrderFileCategories}
                  filesEndpoint={Endpoints.Files}
                  initialFiles={initialFiles}
                  isNewEntity
                  module={JobOrderEntity.module}
                  onAttachmentsChanged={handleFilesChanged}
                  showItemDate
                  showItemMenu
                  viewItemOnClick
                />
              </FPCardContent>
            </FPCard>
          </div>

          {isUserCoach && (
            <div className={globalClasses.newItemsSection}>
              <FPCard>
                <FPCardHeader
                  title="Assign to a Recruiter"
                  subheader="Choose a recruiter to assign this item to."
                  variant="section"
                />

                <FPCardContent variant="relaxed">
                  <div className="inputContainer">
                    <AutocompleteSelect
                      name={FormFieldsMap.Recruiter.key}
                      selectedValue={selectedRecruiter}
                      placeholder="Recruiters in your team"
                      displayKey="full_name"
                      url={`${Endpoints.Recruiters}/myTeam`}
                      onSelect={handleRecruiterSelect}
                    />
                  </div>
                </FPCardContent>
              </FPCard>
            </div>
          )}

          <div className={globalClasses.newItemsActions}>
            <When condition={isDatabaseDrafts && !!databaseDraftUpdatedAt}>
              <Typography variant="body2">
                {strings.formatString(strings.drafts.lastSaved, {
                  updatedAt: databaseDraftUpdatedAt
                })}
              </Typography>
            </When>
            <FPButtonBuilder
              buttonDefinitions={buttonDefinitions}
              GridContainerProps={{ spacing: 2, justify: 'flex-end' }}
            />
          </div>
        </form>
      </FormContext>
      {currentCompanyId && uiState.isWhitesheetOpen && (
        <Drawer open onClose={toggleDrawer('isWhitesheetOpen', false)}>
          <AssignmentSheetDrawer
            key={currentCompanyId}
            drawerTitle={assignmentSheetStrings.title}
            assignmentSheetId={assignmentSheet.current?.id ?? draft?.assignmentSheet?.id}
            whiteSheet={whitesheet}
            companyId={currentCompanyId || 0}
            onClose={toggleDrawer('isWhitesheetOpen', false)}
            onSaveAssignmentSheet={handleSaveAssignmentSheet}
          />
        </Drawer>
      )}

      <When condition={uiState.isHAOpen}>
        <HiringAuthorityDrawer
          entityTitle="Job Order"
          type={EntityType.Company}
          companyId={currentCompanyId}
          endpoint={
            currentCompanyId &&
            `${Endpoints.Companies}/${currentCompanyId}/${Endpoints.HiringAuthorities}`
          }
          onHACompleted={handleHiringAuthorityCompleted}
          onHAClosed={toggleDrawer('isHAOpen', false)}
        />
      </When>
    </Fragment>
  );
};
